import { atom } from 'jotai';

import { atomWithLoadable } from 'lib/atoms';
import request from 'lib/request';
import type { NriMatrixResponse } from 'types/api-responses';

/**
 * Selection atom that drives the shared `nriMatrixAtom` loadable. Components
 * that want to view a calculation's matrix set this atom before subscribing
 * to the loadable.
 */
export const nriCalculationIdForMatrixAtom = atom<number | null>(null);

/**
 * Shared NRI matrix loadable. Reads `nriCalculationIdForMatrixAtom` to know
 * which calculation to fetch, then GETs the matrix from
 * `/nri_calculations/:id/calculation_matrix`.
 *
 * The selection-atom + shared-loadable pattern means only one matrix can be
 * loaded at a time. The conversion to an `atomFamily` is a deferred
 * behaviour-change story.
 */
export const [nriMatrixAtom, nriMatrixLoadableAtom] = atomWithLoadable(
  async (get): Promise<NriMatrixResponse | null> => {
    const nriCalculationId = get(nriCalculationIdForMatrixAtom);
    if (!nriCalculationId) {
      return null;
    }

    try {
      const response = await request.get<NriMatrixResponse>(
        `/nri_calculations/${nriCalculationId}/calculation_matrix`
      );
      return !('error' in response) ? response : null;
    } catch (error) {
      console.error('Failed to fetch NRI matrix:', error);
      return null;
    }
  }
);
